import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Skills: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const skillCategories = [
    {
      title: 'Frontend Development',
      skills: [
        { name: 'React', level: 90, color: 'from-blue-500 to-cyan-500' },
        { name: 'TypeScript', level: 85, color: 'from-blue-500 to-cyan-500' },
        { name: 'Next.js', level: 80, color: 'from-blue-500 to-cyan-500' },
        { name: 'Tailwind CSS', level: 95, color: 'from-blue-500 to-cyan-500' },
        { name: 'Bootstrap', level: 85, color: 'from-blue-500 to-cyan-500' },
        { name: 'Material UI', level: 80, color: 'from-blue-500 to-cyan-500' },
        { name: 'CSS', level: 90, color: 'from-blue-500 to-cyan-500' },
      ]
    },
    {
      title: 'Backend Development',
      skills: [
        { name: 'Node.js', level: 85, color: 'from-green-500 to-emerald-500' },
        { name: 'Express.js', level: 85, color: 'from-green-500 to-emerald-500' },
        { name: 'REST APIs', level: 90, color: 'from-green-500 to-emerald-500' },
        { name: 'GraphQL', level: 75, color: 'from-green-500 to-emerald-500' },
      ]
    },
    {
      title: 'Databases & Caching',
      skills: [
        { name: 'MySQL', level: 80, color: 'from-orange-500 to-red-500' },
        { name: 'MongoDB', level: 85, color: 'from-orange-500 to-red-500' },
        { name: 'Redis', level: 70, color: 'from-orange-500 to-red-500' },
        { name: 'PostgreSQL', level: 80, color: 'from-orange-500 to-red-500' },
      ]
    },
    {
      title: 'DevOps & Tools',
      skills: [
        { name: 'Docker', level: 75, color: 'from-purple-500 to-indigo-500' },
        { name: 'Git', level: 90, color: 'from-purple-500 to-indigo-500' },
        { name: 'CI/CD', level: 70, color: 'from-purple-500 to-indigo-500' },
        { name: 'AWS', level: 65, color: 'from-purple-500 to-indigo-500' },
      ]
    },
    {
      title: 'Deployment & Hosting',
      skills: [
        { name: 'Vercel', level: 90, color: 'from-pink-500 to-rose-500' },
        { name: 'Netlify', level: 85, color: 'from-pink-500 to-rose-500' },
        { name: 'Railway', level: 80, color: 'from-pink-500 to-rose-500' },
        { name: 'Render', level: 85, color: 'from-pink-500 to-rose-500' },
        { name: 'Hostinger', level: 75, color: 'from-pink-500 to-rose-500' },
      ]
    }
  ];

  const additionalSkills = [
    'JavaScript (ES6+)', 'HTML5', 'RESTful APIs', 'JWT Authentication',
    'OAuth', 'Webpack', 'Babel', 'Jest', 'React Testing Library',
    'Responsive Design', 'Progressive Web Apps', 'SEO Optimization',
    'Performance Optimization', 'Cross-browser Compatibility',
    'Domain Management', 'SSL Certificates', 'CDN Configuration',
    'Environment Variables', 'Build Optimization'
  ];

  return (
    <section id="skills" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            My <span className="gradient-text">Skills</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A comprehensive overview of the technologies and tools I use to bring ideas to life
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto rounded-full mt-6"></div>
        </motion.div>

        {/* Skill Categories */}
        <div className="space-y-12">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                {category.title}
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skill.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: (categoryIndex * 0.1) + (skillIndex * 0.1) }}
                    className="space-y-2"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">{skill.name}</span>
                      <span className="text-sm text-gray-500">{skill.level}%</span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${skill.level}%` } : {}}
                        transition={{ duration: 1, delay: (categoryIndex * 0.1) + (skillIndex * 0.1) + 0.3 }}
                        className={`h-3 rounded-full bg-gradient-to-r ${skill.color} shadow-sm`}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 bg-white rounded-2xl p-8 shadow-lg"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Additional Skills & Technologies
          </h3>

          <div className="flex flex-wrap gap-3 justify-center">
            {additionalSkills.map((skill, index) => (
              <motion.span
                key={skill}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.3, delay: 1 + (index * 0.05) }}
                className="skill-badge hover:bg-primary-200 transition-colors duration-200 cursor-default"
              >
                {skill}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Learning Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-2xl p-8 border border-primary-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Always Learning
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              I'm constantly expanding my skill set and staying up-to-date with the latest
              technologies and best practices in software development.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <span className="skill-badge bg-yellow-100 text-yellow-800">
                Currently Learning: Kubernetes
              </span>
              <span className="skill-badge bg-green-100 text-green-800">
                Next: Microservices
              </span>
              <span className="skill-badge bg-purple-100 text-purple-800">
                Exploring: AI/ML Integration
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 